import {
    ENV_DEV,
    ENV_PRODUCTION,
    ENV_STAGING,
    URL_PRODUCTION_API_PRIVATE,
    URL_PRODUCTION_IDENTITY,
    URL_PRODUCTION_WAC,
    URL_STAGING_API_PRIVATE,
    URL_STAGING_IDENTITY,
    URL_STAGING_WAC,
    URL_STAGING_WAC_DEPRECATED,
    RECAPTCHA_KEY,
    RECAPTCHA_KEY_TEST
} from "../constants";

import { Url } from "./url";

export namespace Environment {
    const VALID_LOCALES = [
        "cn",
        "cs",
        "de",
        "es",
        "fr",
        "it",
        "ko",
        "hu",
        "nl",
        "ja",
        "pl",
        "br",
        "ro",
        "fi",
        "ru",
        "zh"
    ];

    // Map of bxp-kit locales to equivalent growth-kit locale, as found in
    // https://bitbucket.org/atlassian/growth-kit/src/master/packages/components/up-flow/_locales/
    // NOTE: List compiled using WAC language switcher, as VALID_LOCALES is not accurate
    type LocaleMap = { [key: string]: string };
    const BXP_TO_GROWTH_LOCALE_MAP: LocaleMap = {
        de: "de",
        es: "es",
        fr: "fr",
        it: "it",
        ko: "ko",
        hu: "hu",
        nl: "nl",
        ja: "ja",
        pl: "pl",
        br: "pt-BR",
        ru: "ru",
        zh: "zh"
    };

    export function getSubdomain() {
        const urlParts = Url.getLocation().hostname.split(".");

        if (urlParts[1] === "dev") {
            return "localhost";
        }

        return urlParts[0];
    }

    // eslint-disable-next-line complexity
    export function getEnvironment() {
        switch (getSubdomain()) {
            case "wac":
            case "qa-wac":
            case "author":
            case "proof":
            case "statlas":
            case "truth":
                return ENV_STAGING;
            case "localhost":
            case "local":
                return ENV_DEV;
            case "www":
            case "atlassian":
            case "bitbucket":
            case "sourcetreeapp":
            case "agile":
            case "git":
            case "software":
                return ENV_PRODUCTION;
            default:
                /* for omlet instances */
                return ENV_STAGING;
        }
    }

    export function isProduction() {
        return getEnvironment() === ENV_PRODUCTION;
    }

    export function isDev() {
        return getEnvironment() === ENV_DEV;
    }

    export function isStaging() {
        return getEnvironment() === ENV_STAGING;
    }

    export function getIdentityBaseUrl() {
        if (!isProduction()) {
            return URL_STAGING_IDENTITY;
        } else {
            return URL_PRODUCTION_IDENTITY;
        }
    }

    export function getWacIntegrationUrl() {
        if (!isProduction()) {
            const useQaWAC =
                window.location.hostname.includes("qa-wac") ||
                window.location.hostname.includes("author");

            return useQaWAC ? URL_STAGING_WAC_DEPRECATED : URL_STAGING_WAC;
        } else {
            return URL_PRODUCTION_WAC;
        }
    }

    export function getApiPrivateUrl() {
        if (!isProduction()) {
            return URL_STAGING_API_PRIVATE;
        } else {
            return URL_PRODUCTION_API_PRIVATE;
        }
    }

    export function getHamletUrl() {
        if (!isProduction()) {
            return `${getWacIntegrationUrl()}/apis/stg/hamlet`;
        } else {
            return `${getWacIntegrationUrl()}/hamlet`;
        }
    }

    export function getInternationalSubdomain() {
        const hostnameSubdomain = getSubdomain() || "";
        if (!VALID_LOCALES.includes(hostnameSubdomain)) {
            return false;
        }
        return hostnameSubdomain;
    }

    export function getPathLocale() {
        const pathParts = window.location.pathname.split("/");

        // check for https://author.marketing.internal.atlassian.com/wac/ja
        const localeIndex =
            pathParts.length > 2 && pathParts[1] === "wac" ? 2 : 1;
        return pathParts.length > 1 &&
            VALID_LOCALES.includes(pathParts[localeIndex] || "")
            ? `/${pathParts[localeIndex]}`
            : "";
    }

    // Return a locale that is compatible with growth-kit
    // https://bitbucket.org/atlassian/growth-kit/src/master/packages/components/up-flow/_locales/
    export function getGrowthKitLocale(): string {
        const currentLocale = getPathLocale().substring(1);
        const growthKitLocale = BXP_TO_GROWTH_LOCALE_MAP[currentLocale];
        if (growthKitLocale !== undefined) {
            return growthKitLocale;
        }
        return "";
    }

    type GetCurrentSearchParamOptions = {
        readonly bundle?: string;
        readonly product?: string;
        readonly edition?: string;
        readonly templateId?: string;
        readonly migrationSourceUuid?: string;
        readonly developer?: string;
        readonly src?: string;
    };

    export function getCurrentSearchParam({
        bundle,
        product,
        edition,
        templateId,
        migrationSourceUuid,
        developer,
        src
    }: GetCurrentSearchParamOptions) {
        const location = Url.getLocation();
        const internationalSubdomain = getInternationalSubdomain();

        const params = new URLSearchParams(location.search.slice(1));

        if (internationalSubdomain) {
            params.set("lang", internationalSubdomain);
        }

        if (bundle) {
            params.set("bundle", bundle);
        }

        if (product) {
            params.set("product", product);
        }

        if (edition) {
            params.set("edition", edition);
        }

        if (templateId) {
            params.set("templateId", templateId);
        }

        if (migrationSourceUuid) {
            params.set("migrationSourceUuid", migrationSourceUuid);
        }

        if (developer) {
            params.set("developer", developer);
        }

        if (src) {
            params.set("src", src);
        }

        return "?" + params.toString();
    }

    export function getParam(param: string) {
        const params = new URLSearchParams(location.search.slice(1));
        return params.get(param);
    }

    type GetSocialSignUpUrlOptions = {
        readonly loginGoogleOrigin: string;
        readonly socialContinueUrl: string;
        readonly bundle?: string;
        readonly product?: string;
        readonly edition?: string;
        readonly templateId?: string;
        readonly migrationSourceUuid?: string;
        readonly developer?: string;
        readonly src?: string;
        readonly continueUrlOverride?: string;
    };

    export function getSocialSignUpUrl({
        loginGoogleOrigin,
        socialContinueUrl,
        bundle,
        product,
        edition,
        templateId,
        migrationSourceUuid,
        developer,
        src,
        continueUrlOverride
    }: GetSocialSignUpUrlOptions) {
        const splitPath = socialContinueUrl.split("/");

        if (splitPath.length >= 2 && splitPath[1]?.includes("wac")) {
            // remove contextual path if applicable
            splitPath.splice(0, 2);
            socialContinueUrl = `/${splitPath.join("/")}`;
        }
        socialContinueUrl = `${getWacIntegrationUrl()}${socialContinueUrl}`;

        const signupSocialUrl = `${socialContinueUrl}${getCurrentSearchParam({
            bundle,
            product,
            edition,
            templateId,
            migrationSourceUuid,
            developer,
            src
        })}`;
        const cobranding = `application=wac`;
        const socialSignup = `&social-signup-login=true`;
        const atlOrigin = `&atlOrigin=${encodeURIComponent(loginGoogleOrigin)}`;
        let continueUrl = `&continue=${encodeURIComponent(
            `${signupSocialUrl}${socialSignup}`
        )}`;
        const accountPrompt = `&prompt=select_account`;

        if (templateId && continueUrlOverride) {
            continueUrl = `&continue=${encodeURIComponent(
                continueUrlOverride
            )}`;
        }
        return `${getIdentityBaseUrl()}/login/initiate/google?${cobranding}${atlOrigin}${continueUrl}${accountPrompt}`;
    }

    export function getIdSigninUrl(
        atlOriginId?: string,
        skipEncoding?: boolean,
        continueUrlOverride?: string
    ) {
        let currentUrl = skipEncoding
            ? Url.getUrl()
            : encodeURIComponent(Url.getUrl());
        if (continueUrlOverride) {
            currentUrl = skipEncoding
                ? continueUrlOverride
                : encodeURIComponent(continueUrlOverride);
        }
        const returnUrl = atlOriginId
            ? `${getIdentityBaseUrl()}/login?atlOrigin=${atlOriginId}&continue=${currentUrl}`
            : `${getIdentityBaseUrl()}/login?continue=${currentUrl}`;
        return returnUrl;
    }

    export function getIdSwitchUserUrl(atlOriginId?: string) {
        const idSigninUrl = new URL(getIdSigninUrl(atlOriginId, false));
        idSigninUrl.searchParams.set("application", "wac");
        idSigninUrl.searchParams.set("prompt", "select_account");
        return idSigninUrl.toString();
    }

    export const reCaptchaKey = isProduction()
        ? RECAPTCHA_KEY
        : RECAPTCHA_KEY_TEST;
}
